import * as React from 'react'
import { useState } from 'react'

import { useMutation } from '@apollo/client'
import { Box, Button, Checkbox, Text } from '@factorialco/deprecated-design-system'
import {
  Input,
  RichTextEditor,
  RichTextEditorHandle,
  Select,
  Textarea,
} from '@factorialco/f0-react/dist/experimental'
import { SUPPORTED_LANGUAGES_LABELS } from 'config/locales'
import {
  CreateItManagementDocumentTemplateDocument,
  ItManagementDocumentTemplatesListDocument,
  UpdateItManagementDocumentTemplateDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

interface TemplateFormModalProps {
  template?: {
    id: string
    title: string
    templateType: string
    language: string
    twoLanguages: boolean
    languageSecond?: string | null
    policyText?: string | null
    policyTextSecondLanguage?: string | null
  }
  onClose: () => void
}

// Available languages for document templates
const LANGUAGE_OPTIONS = Object.entries(SUPPORTED_LANGUAGES_LABELS)
  .filter(([code]) => !['en', 'eo'].includes(code)) // Exclude developer and test languages
  .map(([value, label]) => ({ value, label }))

export const TemplateFormModal: React.FC<TemplateFormModalProps> = ({ template, onClose }) => {
  const isEditing = !!template

  const policyTextEditorRef = React.useRef<RichTextEditorHandle>(null)
  const policyTextSecondLanguageEditorRef = React.useRef<RichTextEditorHandle>(null)

  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  const minimalToolbarLabels = {
    bold: i18n.t('rich_text_editor.toolbar.bold'),
    italic: i18n.t('rich_text_editor.toolbar.italic'),
    underline: i18n.t('rich_text_editor.toolbar.underline'),
    strike: i18n.t('rich_text_editor.toolbar.strike'),
    highlight: i18n.t('rich_text_editor.toolbar.highlight'),
    heading1: i18n.t('rich_text_editor.toolbar.heading1'),
    heading2: i18n.t('rich_text_editor.toolbar.heading2'),
    heading3: i18n.t('rich_text_editor.toolbar.heading3'),
    left: i18n.t('rich_text_editor.toolbar.left'),
    center: i18n.t('rich_text_editor.toolbar.center'),
    right: i18n.t('rich_text_editor.toolbar.right'),
    justify: i18n.t('rich_text_editor.toolbar.justify'),
    bulletList: i18n.t('rich_text_editor.toolbar.bulletList'),
    orderedList: i18n.t('rich_text_editor.toolbar.orderedList'),
    taskList: i18n.t('rich_text_editor.toolbar.taskList'),
    codeBlock: i18n.t('rich_text_editor.toolbar.codeBlock'),
    horizontalRule: i18n.t('rich_text_editor.toolbar.horizontalRule'),
    quote: i18n.t('rich_text_editor.toolbar.quote'),
    moreOptions: i18n.t('rich_text_editor.toolbar.moreOptions'),
    code: i18n.t('rich_text_editor.toolbar.code'),
    divider: i18n.t('rich_text_editor.toolbar.divider'),
    bullet: i18n.t('rich_text_editor.toolbar.bullet'),
    ordered: i18n.t('rich_text_editor.toolbar.ordered'),
    task: i18n.t('rich_text_editor.toolbar.task'),
    linkPlaceholder: i18n.t('rich_text_editor.toolbar.linkPlaceholder'),
    linkLabel: i18n.t('rich_text_editor.toolbar.linkLabel'),
    linkPaste: i18n.t('rich_text_editor.toolbar.linkPaste'),
    close: i18n.t('rich_text_editor.toolbar.close'),
  }

  const minimalEditorConfig = {
    ...editorConfig,
    toolbarLabels: minimalToolbarLabels,
  }

  const [formData, setFormData] = useState({
    title: template?.title || '',
    templateType: template?.templateType || 'assign',
    language: template?.language || 'en-us',
    twoLanguages: template?.twoLanguages || false,
    languageSecond: template?.languageSecond || '',
    policyText: template?.policyText || '',
    policyTextSecondLanguage: template?.policyTextSecondLanguage || '',
  })

  const [createTemplate, { loading: creating }] = useMutation(
    CreateItManagementDocumentTemplateDocument,
    {
      refetchQueries: [ItManagementDocumentTemplatesListDocument],
      onCompleted: () => {
        onClose()
      },
    }
  )

  const [updateTemplate, { loading: updating }] = useMutation(
    UpdateItManagementDocumentTemplateDocument,
    {
      refetchQueries: [ItManagementDocumentTemplatesListDocument],
      onCompleted: () => {
        onClose()
      },
    }
  )

  const handleSubmit = () => {
    if (isEditing) {
      updateTemplate({
        variables: {
          id: template.id,
          ...formData,
        },
      })
    } else {
      createTemplate({
        variables: formData,
      })
    }
  }

  const loading = creating || updating
  const isDisabled = !formData.title || (formData.twoLanguages && !formData.languageSecond)

  return (
    <Modal
      data-testid="template-form-modal"
      title={
        isEditing
          ? i18n.t('it_management.settings.document_templates.edit_modal.title')
          : i18n.t('it_management.settings.document_templates.create_modal.title')
      }
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Button type="secondary" onClick={onClose} disabled={loading}>
            {i18n.t('common.cancel')}
          </Button>

          <Button type="primary" submit disabled={isDisabled}>
            {isEditing
              ? i18n.t('common.save')
              : i18n.t('it_management.settings.document_templates.create_modal.confirm')}
          </Button>
        </Box>
      }
    >
      <Box gap="s16">
        <Input
          label={i18n.t('it_management.settings.document_templates.form.title')}
          value={formData.title}
          onChange={(value: string) => setFormData({ ...formData, title: value })}
          required
        />

        <Select
          label={i18n.t('it_management.settings.document_templates.form.language')}
          value={formData.language}
          onChange={(value: string) => setFormData({ ...formData, language: value })}
          options={LANGUAGE_OPTIONS}
          required
        />

        <Box gap="s8">
          <Text.Normal size="200" weight="medium" color="grey800">
            {i18n.t('it_management.settings.document_templates.form.template_type')}
          </Text.Normal>
          <Box flexDirection="row" gap="s16">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="templateType"
                value="assign"
                checked={formData.templateType === 'assign'}
                onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
              />
              <Text.Normal size="200" color="grey800">
                {i18n.t('it_management.settings.document_templates.types.assign')}
              </Text.Normal>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="templateType"
                value="unassign"
                checked={formData.templateType === 'unassign'}
                onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
              />
              <Text.Normal size="200" color="grey800">
                {i18n.t('it_management.settings.document_templates.types.unassign')}
              </Text.Normal>
            </label>
          </Box>
        </Box>

        {richTextEditorLoading ? (
          <RichTextEditor.Skeleton />
        ) : canUseRichTextEditor ? (
          <Box gap="s8">
            <Text.Normal size="200" weight="medium" color="grey800">
              {i18n.t('it_management.settings.document_templates.form.policy_text')}
            </Text.Normal>
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
            >
              <RichTextEditor
                ref={policyTextEditorRef}
                {...minimalEditorConfig}
                onChange={(data) => {
                  setFormData({ ...formData, policyText: data?.value || '' })
                }}
                placeholder={i18n.t('it_management.settings.document_templates.form.policy_text')}
                initialEditorState={{ content: formData.policyText || '' }}
                title={i18n.t('it_management.settings.document_templates.form.policy_text')}
              />
            </div>
          </Box>
        ) : (
          <Textarea
            label={i18n.t('it_management.settings.document_templates.form.policy_text')}
            value={formData.policyText}
            onChange={(value: string) => setFormData({ ...formData, policyText: value })}
            rows={3}
          />
        )}

        <Box flexDirection="row" gap="s12" alignItems="center">
          <Checkbox
            checked={formData.twoLanguages}
            onChange={(e) => setFormData({ ...formData, twoLanguages: e.target.checked })}
            size="large"
          />
          <Text.Normal size="200" weight="medium" color="grey800">
            {i18n.t('it_management.settings.document_templates.form.two_languages')}
          </Text.Normal>
        </Box>

        {formData.twoLanguages && (
          <>
            <Select
              label={i18n.t('it_management.settings.document_templates.form.language_second')}
              value={formData.languageSecond}
              onChange={(value: string) => setFormData({ ...formData, languageSecond: value })}
              options={LANGUAGE_OPTIONS}
              required
            />

            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              <Box gap="s8">
                <Text.Normal size="200" weight="medium" color="grey800">
                  {i18n.t(
                    'it_management.settings.document_templates.form.policy_text_second_language'
                  )}
                </Text.Normal>
                <div
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                >
                  <RichTextEditor
                    ref={policyTextSecondLanguageEditorRef}
                    {...minimalEditorConfig}
                    onChange={(data) => {
                      setFormData({
                        ...formData,
                        policyTextSecondLanguage: data?.value || '',
                      })
                    }}
                    placeholder={i18n.t(
                      'it_management.settings.document_templates.form.policy_text_second_language'
                    )}
                    initialEditorState={{
                      content: formData.policyTextSecondLanguage || '',
                    }}
                    title={i18n.t(
                      'it_management.settings.document_templates.form.policy_text_second_language'
                    )}
                  />
                </div>
              </Box>
            ) : (
              <Textarea
                label={i18n.t(
                  'it_management.settings.document_templates.form.policy_text_second_language'
                )}
                value={formData.policyTextSecondLanguage}
                onChange={(value: string) =>
                  setFormData({ ...formData, policyTextSecondLanguage: value })
                }
                rows={3}
              />
            )}
          </>
        )}
      </Box>
    </Modal>
  )
}
